import { useEffect, useMemo, useState } from 'react';
import { getPriceMatches, applyPriceUpdates, type PriceMatch } from '@/lib/api/expenses';
import { useToast } from '@/lib/hooks/useToast';

const money = (n: number | null) =>
  n == null ? '—' : new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(n);

// A match is pre-selected when it's an exact SKU match or a strong name match
// AND the price actually differs — the safe defaults for a review-and-apply flow.
const defaultChecked = (m: PriceMatch) =>
  m.price_changed && (m.match_type === 'sku' || m.confidence >= 0.7);

interface Props {
  jobId: string | number;
  expenseId: number;
  onClose: () => void;
  onApplied: () => void;
}

export function CatalogPriceReviewModal({ jobId, expenseId, onClose, onApplied }: Props) {
  const { toast } = useToast();
  const [matches, setMatches] = useState<PriceMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    getPriceMatches(jobId, expenseId)
      .then(m => {
        setMatches(m);
        const init: Record<number, boolean> = {};
        m.forEach((match, i) => { init[i] = defaultChecked(match); });
        setSelected(init);
      })
      .catch(() => toast.error('Could not match this receipt to the catalog'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, expenseId]);

  const changed = useMemo(() => matches.filter(m => m.price_changed), [matches]);
  const selectedCount = Object.values(selected).filter(Boolean).length;

  const apply = async () => {
    const updates = matches
      .map((m, i) => ({ m, i }))
      .filter(({ i }) => selected[i])
      .map(({ m }) => ({ product_id: m.product_id, unit_price: m.receipt_unit_price, expense_id: expenseId }));
    if (updates.length === 0) { toast.error('Select at least one price to update'); return; }
    setApplying(true);
    try {
      const { applied } = await applyPriceUpdates(updates);
      toast.success(`${applied} catalog price${applied === 1 ? '' : 's'} updated`);
      onApplied();
      onClose();
    } catch {
      toast.error('Failed to update catalog prices');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Update catalog pricing</h3>
            <p className="text-xs text-gray-500">Review matches from this receipt, then apply.</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading ? (
            <div className="space-y-2">
              {[0, 1, 2].map(i => <div key={i} className="h-14 animate-pulse bg-gray-100 rounded-lg" />)}
            </div>
          ) : changed.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              <span className="material-symbols-outlined text-4xl text-gray-300">price_check</span>
              <p className="text-sm mt-2">No catalog price changes found for this receipt.</p>
              <p className="text-xs text-gray-400 mt-1">
                Either the items already match the catalog price, or they couldn't be matched to a product.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {matches.map((m, i) => {
                if (!m.price_changed) return null;
                const up = m.current_price != null && m.receipt_unit_price > m.current_price;
                return (
                  <label key={i} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!selected[i]}
                      onChange={e => setSelected(s => ({ ...s, [i]: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 truncate">{m.product_name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {m.product_sku ? `SKU ${m.product_sku} · ` : ''}from receipt: “{m.line_description}”
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm">
                        <span className="text-gray-400 line-through mr-1">{money(m.current_price)}</span>
                        <span className={`font-bold ${up ? 'text-red-600' : 'text-green-700'}`}>{money(m.receipt_unit_price)}</span>
                      </div>
                      <span className={`inline-block mt-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                        m.match_type === 'sku' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {m.match_type === 'sku' ? 'SKU match' : `name ${Math.round(m.confidence * 100)}%`}
                      </span>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {!loading && changed.length > 0 && (
          <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-gray-100">
            <div className="flex gap-2 text-xs">
              <button onClick={() => setSelected(Object.fromEntries(matches.map((_, i) => [i, matches[i].price_changed])))} className="text-primary hover:underline">Select all</button>
              <span className="text-gray-300">·</span>
              <button onClick={() => setSelected({})} className="text-gray-500 hover:underline">Clear</button>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={onClose} className="h-9 px-4 rounded-lg text-sm text-gray-600 hover:bg-gray-100">Cancel</button>
              <button
                onClick={apply}
                disabled={applying || selectedCount === 0}
                className="h-9 px-4 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-60"
              >
                {applying ? 'Applying…' : `Apply ${selectedCount || ''} update${selectedCount === 1 ? '' : 's'}`.trim()}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
